// The stop dialog's payment choices, turned into what the stop route expects.
// Mirrors bill_shares in backend/app/services/dues.py, whose own bill decides the
// amounts in the end: the clock can tick on while the dialog is open.
import { roundToPaise } from "./format";

export const TABLE_SHARE = "table";

export const PAYMENT_FULL = "full";
export const PAYMENT_PART = "part";
export const PAYMENT_LATER = "later";

// The parts of a bill that can each be paid or owed separately. An ordinary game
// is one part. On loser pays each side has its own, and whatever nobody lost
// (table items, the unfinished frame) is a part of its own.
export function billShares(amountDue, tally) {
  const sides = (tally?.sides || [])
    .filter((side) => side.total > 0)
    .map((side) => ({
      id: side.sideId,
      label: side.isTeam ? `${side.name} (${side.members.map((m) => m.name).join(" + ")})` : side.name,
      amount: roundToPaise(side.total),
      // A team's label is rarely who you'd chase for the money
      name: side.isTeam ? "" : side.name,
    }));
  const rest = roundToPaise(amountDue - sides.reduce((sum, s) => sum + s.amount, 0));
  if (sides.length === 0) return [{ id: TABLE_SHARE, label: null, amount: rest, name: "" }];
  return rest > 0 ? [...sides, { id: TABLE_SHARE, label: "On the table", amount: rest, name: "" }] : sides;
}

const paidNow = (choice) => (choice.mode === PAYMENT_PART ? Number(choice.paid) || 0 : 0);

// What this share leaves unpaid, for showing while the choice is being made.
export function leftToPay(share, choice) {
  if (!choice || choice.mode === PAYMENT_FULL) return 0;
  return Math.max(0, roundToPaise(share.amount - paidNow(choice)));
}

// Why the dialog can't be confirmed yet, or null when it can.
export function paymentProblem(shares, choices) {
  for (const share of shares) {
    const choice = choices[share.id];
    if (!choice || choice.mode === PAYMENT_FULL) continue;
    if (choice.mode === PAYMENT_PART) {
      const paid = Number(choice.paid);
      if (!paid || paid <= 0) return "Enter the amount paid";
      if (paid >= share.amount) return "That covers the whole bill. Choose Paid";
    }
    if (!(choice.name ?? share.name).trim()) return "Enter whose name to put it on";
  }
  return null;
}

// Sent when the game stops. Empty means everyone paid in full.
export function duesToPost(shares, choices) {
  return shares.flatMap((share) => {
    const choice = choices[share.id];
    if (!choice || choice.mode === PAYMENT_FULL) return [];
    return [{ share: share.id, name: (choice.name ?? share.name).trim(), paid_now: paidNow(choice) }];
  });
}

// Accounts whose name contains what has been typed, for suggesting while typing.
export function matchingAccounts(accounts, typed, limit = 4) {
  const query = typed.trim().toLowerCase();
  if (!query) return [];
  return accounts
    .filter((a) => a.name.toLowerCase().includes(query) && a.name.toLowerCase() !== query)
    .slice(0, limit);
}

export const accountNamed = (accounts, name) => {
  const key = name.trim().replace(/\s+/g, " ").toLowerCase();
  return accounts.find((a) => a.name.toLowerCase() === key);
};
